import { runInAction } from "mobx";

import { AnalyticsModuleClient } from "@formant/core/client/AnalyticsModuleClient";
import { defined } from "@formant/core/common/defined";
import { IAnalyticsModule } from "@formant/core/model/IAnalyticsModule";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class AnalyticsModuleStore {
    private listCache = new StoreCache<undefined, IAnalyticsModule[]>();
    private cache = new StoreCache<Uuid, IAnalyticsModule | false>();

    constructor(
        private analyticsModuleClient: AnalyticsModuleClient,
        private authStore: IAuthStore,
    ) {}

    public listAll(): IAnalyticsModule[] | undefined {
        return [
            ...(this.listCache.get(
                undefined,
                async () => await this.fetchList(),
            ) || []),
        ];
    }

    public get(id: string): IAnalyticsModule | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public fetch(id: string): Promise<IAnalyticsModule | false> {
        return this.fetchGet(id);
    }

    public async create(
        analyticsModule: IAnalyticsModule,
    ): Promise<IAnalyticsModule> {
        const created = await this.analyticsModuleClient.create(
            await this.authStore.token(),
            analyticsModule,
        );
        this.cache.set(defined(created.id), created);
        return created;
    }

    public async update(id: Uuid, analyticsModule: Partial<IAnalyticsModule>) {
        const updated = await this.analyticsModuleClient.update(
            await this.authStore.token(),
            id,
            analyticsModule,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(analyticsModule: IAnalyticsModule): Promise<IAnalyticsModule> {
        const { id } = analyticsModule;
        if (id) {
            return this.update(id, analyticsModule);
        } else {
            return this.create(analyticsModule);
        }
    }

    public async delete(id: Uuid) {
        const previous = await this.fetchList();

        await this.analyticsModuleClient.delete(
            await this.authStore.token(),
            id,
        );

        runInAction(() => {
            this.cache.set(id, false);
            if (previous) {
                this.listCache.set(
                    undefined,
                    previous.filter(_ => _.id !== id),
                );
            }
        });
    }

    public async fetchGet(id: Uuid): Promise<IAnalyticsModule | false> {
        const analyticsModule = await this.analyticsModuleClient.get(
            await this.authStore.token(),
            id,
        );
        const value = analyticsModule || false;
        this.cache.set(id, value);
        return value;
    }

    public async fetchList(): Promise<IAnalyticsModule[]> {
        const value = await this.analyticsModuleClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
